import {SummaryReport, SummaryReportData} from '@/types/reports/Report'

export const OPERATIONAL_INCOME = 'Driftsinntekter'
export const SALES_INCOME = 'Salgsinntekt'
export const OTHER_INCOME = 'Annen driftsinntekt'
export const OPERATING_COSTS = 'Varekostnad'
export const PERSONNEL_COSTS = 'Lønnskostnad'
export const DEPRECIATIONS = 'Av/ned skrivning på varige driftsmidler og immaterielle eiendeler'
export const OTHER_OPERATING_COSTS = 'Annen driftskostnad'
export const FINANCIAL_INCOME = 'Finansinntekter'
export const FINANCIAL_EXPENSES = 'Finanskostnader'
export const FINANCIAL_VALUE_CHANGE = 'Verdiendring av finansielle instrumenter vurdert til virkelig verdi'
export const OPERATIONAL_EXPENSES = 'Driftskostnader'

export type ProfLosMetric = {
    title: string,
    description?: string,
    amount: number,
    rate?: number,
    condition?: Function
}

export class ProfLosReport extends SummaryReport {
    constructor(data: SummaryReportData) {
        super(data)
    }

    operationalIncome(): ProfLosMetric {
        return {
            title: 'Driftsinntekt',
            amount: (this.getGroupByName(OPERATIONAL_INCOME)?.outgoingBalance || 0) * -1,
            description: ''
        }
    }

    salesIncome(): ProfLosMetric {
        return {
            title: 'Salgsinntekt',
            amount: (this.getGroupByName(SALES_INCOME)?.outgoingBalance || 0) * -1,
            description: ''
        }
    }

    otherIncome(): ProfLosMetric {
        return {
            title: 'Annen driftsinntekt',
            amount: (this.getGroupByName(OTHER_INCOME)?.outgoingBalance || 0) * -1,
            description: ''
        }
    }

    operatingCosts(): ProfLosMetric {
        const amount = this.getGroupByName(OPERATING_COSTS)?.outgoingBalance || 0
        return {
            title: 'Varekostnad',
            amount,
            rate: (amount / (this.operationalIncome().amount || 0)) * 100,
            description: ''
        }
    }

    personnelCosts(): ProfLosMetric {
        const amount = this.getGroupByName(PERSONNEL_COSTS)?.outgoingBalance || 0
        return {
            title: 'Lønnskostnad',
            amount,
            rate: (amount / (this.operationalIncome().amount || 0)) * 100,
            description: ''
        }
    }

    contributionMargin(): ProfLosMetric {
        const amount = this.operationalIncome().amount - this.operatingCosts().amount - this.personnelCosts().amount
        return {
            title: 'Dekningsbidrag',
            amount,
            rate: (amount / (this.operationalIncome().amount || 0)) * 100,
            description: 'Salgsinntekter fratrukket varekostnader og lønnskostnader.',
            condition: () => this.operationalIncome().amount !== 0
        }
    }

    otherOperatingCosts(): ProfLosMetric {
        const amount = this.getGroupByName(OTHER_OPERATING_COSTS)?.outgoingBalance || 0
        return {
            title: 'Annen driftskostnad',
            amount,
            rate: (amount / (this.operationalIncome().amount || 0)) * 100
        }
    }

    ebitda(): ProfLosMetric {
        const amount = this.contributionMargin().amount - this.otherOperatingCosts().amount
        return {
            title: 'EBITDA',
            amount,
            rate: (amount / (this.operationalIncome().amount || 0)) * 100,
            description: 'Dekningsbidrag fratrukket andre driftskostnader, utenom av- og nedskrivninger.'
        }
    }

    depreciations(): ProfLosMetric {
        const amount = this.getGroupByName(DEPRECIATIONS)?.outgoingBalance || 0
        return {
            title: 'Av- og nedskrivninger',
            amount,
            rate: (amount / (this.operationalIncome().amount || 0)) * 100
        }
    }

    ebit(): ProfLosMetric {
        const amount = this.contributionMargin().amount - this.depreciations().amount - this.otherOperatingCosts().amount
        return {
            title: 'Driftsresultat (EBIT)',
            amount,
            rate: (amount / (this.operationalIncome().amount || 0)) * 100,
            description: 'Dekningsbidrag fratrukket andre driftskostnader, inkludert av- og nedskrivninger.'
        }
    }

    netFinance(): ProfLosMetric {
        const financeIncome = (this.getGroupByName(FINANCIAL_INCOME)?.outgoingBalance || 0) * -1
        const financeValueChange = this.getGroupByName(FINANCIAL_VALUE_CHANGE)?.outgoingBalance || 0
        const financeExpense = this.getGroupByName(FINANCIAL_EXPENSES)?.outgoingBalance || 0
        const amount = financeIncome + financeValueChange + financeExpense
        return {
            title: 'Netto finans',
            amount,
            rate: (amount / (this.operationalIncome().amount || 0)) * 100,
            description: 'Finansinntekter minus finanskostnader. Dersom finanskostnadene er høyere enn finansinntektene vil beløpet presenteres som et negativt beløp.'
        }
    }

    netIncome(): ProfLosMetric {
        const ebit = this.ebit().amount
        const depreciations = this.depreciations().amount
        const financeExpense = this.getGroupByName(FINANCIAL_EXPENSES)?.outgoingBalance || 0
        const financeIncome = (this.getGroupByName(FINANCIAL_INCOME)?.outgoingBalance || 0) * -1
        const amount = ebit - depreciations - financeExpense + financeIncome
        return {
            title: 'Resultat før skatt',
            amount,
            rate: (amount / (this.operationalIncome().amount || 0)) * 100,
            description: 'Driftsresultat fratrukket netto finans.'
        }
    }

    operationalExpenses(): ProfLosMetric {
        const amount = this.getGroupByName(OPERATIONAL_EXPENSES)?.outgoingBalance || 0
        return {
            title: 'Kostnader',
            amount,
            rate: (amount / (this.operationalIncome().amount || 0)) * 100,
            description: 'Totale kostnader, inkludert varekostnad, lønnskostnad, av- og nedskrivninger, andre driftskostnader og finanskostnader.'
        }
    }
}